import _ from "lodash";
import { usePopupState } from "material-ui-popup-state/hooks";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "src/store";
import { ProgressBar } from "src/ui/shared/ProgressBar";

import filesApiClient from "@dashboard/files/api/apiClient";
import { useFetchProductByIdQuery } from "@dashboard/products/api";
import { Build } from "@dashboard/products/types";
import { useCurrentSubscriptionExpired } from "@dashboard/workspaces/api/helpers";
import { Box, Popover, Typography, useTheme } from "@mui/material";
import { SecondaryButton } from "@shared/CustomButton";
import { LoadingIcon } from "@shared/Loading";
import { Modal } from "@shared/Modal/Modal";
import { showErrorToast } from "@shared/toasts/Toasts";
import { Tooltip } from "@shared/Tooltip";

import { getArtifactIcon } from "../Artifacts";
import { useStyles } from "../Cells.style";
import { countDownloads } from "../countDownloads";

import { useExportFile } from "./DownloadActionsMenu.utils";

export const DownloadActionsMenu = (props: { build: Build }) => {
    const [downloadingArtifactId, setDownloadingArtifactId] = React.useState<number | null>(null);

    const theme = useTheme();
    const dispatch = useDispatch();

    const { classes, cx } = useStyles();
    const { t } = useTranslation(["common", "modals"]);
    const exportFile = useExportFile(props.build);

    const menuAnchorRef = React.useRef<HTMLButtonElement>(null);
    const popupState = usePopupState({ variant: "popover", popupId: "file-download-menu" });

    const { data: product } = useFetchProductByIdQuery(props.build.productId);

    const isExpired = useCurrentSubscriptionExpired();

    const handleArtifactDownload = async (artifact: Build["artifacts"][number]) => {
        setDownloadingArtifactId(artifact.id);
        try {
            await filesApiClient.invokeFileDownload(artifact.id, artifact.name ?? "", true);
            dispatch(countDownloads(props.build, [artifact.id]));
        } catch (error) {
            showErrorToast((error as Error).message);
        } finally {
            setDownloadingArtifactId(null);
        }
    };

    const downloadableArtifacts = props.build.artifacts.filter((item) => item.downloadable && item.isUploaded);

    const sortedArtifacts = _.orderBy(downloadableArtifacts, "name");
    const hasDownloadableArtifacts = downloadableArtifacts.length > 0;

    const getDownloadTooltip = () => {
        if (isExpired) {
            return t("common:section.downloadActionsMenu.tooltip.subscriptionExpired");
        }
        if (!hasDownloadableArtifacts) {
            return t("common:section.downloadActionsMenu.nothingToDownload");
        }
        return t("common:section.downloadActionsMenu.tooltip.downloadResults");
    };

    return (
        <React.Fragment>
            <Tooltip title={getDownloadTooltip()}>
                <SecondaryButton
                    ref={menuAnchorRef}
                    onClick={() => popupState.open(menuAnchorRef.current)}
                    disabled={!hasDownloadableArtifacts || isExpired}
                >
                    <i className="fa-solid fa-download" />
                </SecondaryButton>
            </Tooltip>
            <Popover
                open={popupState.isOpen}
                anchorEl={popupState.anchorEl}
                onClose={() => popupState.close()}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
            >
                <Box className={classes.popover}>
                    <Box className={classes.popoverHeader}>
                        <Typography variant="h6" style={{ fontWeight: 600, fontSize: "1rem" }}>
                            {t("common:section.downloadActionsMenu.buildResults")}
                        </Typography>
                    </Box>
                    <Box className={classes.popoverContent}>
                        {(() => {
                            if (sortedArtifacts.length === 0) {
                                return (
                                    <Typography
                                        variant="body1"
                                        color="textSecondary"
                                        style={{ padding: theme.spacing(2), textAlign: "center" }}
                                    >
                                        {t("common:section.downloadActionsMenu.nothingToDownload")}
                                    </Typography>
                                );
                            }

                            return sortedArtifacts.map((item) => {
                                const { icon, tooltip } = getArtifactIcon(item);
                                const isDownloading = downloadingArtifactId === item.id;

                                return (
                                    <Box
                                        key={item.id}
                                        component="button"
                                        type="button"
                                        onClick={() => handleArtifactDownload(item)}
                                        disabled={downloadingArtifactId !== null}
                                        className={classes.artifactItem}
                                    >
                                        {isDownloading ? (
                                            <span className={classes.artifactIcon}>
                                                <LoadingIcon />
                                            </span>
                                        ) : (
                                            <Tooltip title={tooltip}>
                                                <i className={`${icon} ${classes.artifactIcon}`} />
                                            </Tooltip>
                                        )}
                                        <Typography className={classes.artifactName} variant="body1">
                                            {item.name}
                                        </Typography>
                                    </Box>
                                );
                            });
                        })()}
                        {product?.visibility?.includes("ExportableBuilds") ? (
                            <Box className={classes.exportSection}>
                                <SecondaryButton
                                    onClick={() => {
                                        popupState.close();
                                        exportFile.onExport();
                                    }}
                                    className={classes.exportButton}
                                >
                                    <i className={cx("fa-solid fa-file-arrow-down", classes.exportIcon)} />
                                    {t("common:button.export")}
                                </SecondaryButton>
                            </Box>
                        ) : null}
                    </Box>
                </Box>
            </Popover>
            <Modal
                open={!!exportFile.name}
                title={t("modals:downloadingFiles.title")}
                endButton={<SecondaryButton onClick={exportFile.onAbort}>{t("common:button.cancel")}</SecondaryButton>}
            >
                <Box style={{ display: "flex", flexDirection: "column", gap: theme.spacing(1) }}>
                    <Typography variant="body1">{exportFile.name}</Typography>
                    <ProgressBar progress={exportFile.progress} color={theme.palette.emteria.primary} />
                    <Typography variant="body1">{exportFile.speed}</Typography>
                </Box>
            </Modal>
        </React.Fragment>
    );
};
